/**
 * SelectNowResultsTab.jsx
 * 
 * DAS-1856: Tab component for browsing saved Select Now results.
 * Allows users to view, search, and open previously saved recommendations.
 */



import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getPortfolioData } from '../../redux/reducers/portfolioReducer';
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  message,
  Spin,
  Tag,
  Tooltip,
  Popconfirm,
  Input,
  Empty,
  Descriptions,
  Statistic,
  Row,
  Col,
  Drawer,
  Modal,
  Badge,
  Alert,
  Collapse,
} from 'antd';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import { Dialog, DialogTitle, DialogContent } from '@mui/material';
import AlertCard from '../AlertCard';
// DAS-2327: shared tooltip content for Return Prob / Exp Q / Reward÷Risk columns
import {
  ExpQReturnTooltip,
  ReturnProbabilityTooltip,
  RewardRiskTooltip,
  ExpQReturnHeaderTooltip,
  ReturnProbabilityHeaderTooltip,
  RewardRiskHeaderTooltip,
} from '../../helpers/returnMetricsTooltips';
import {
  ReloadOutlined,
  EyeOutlined,
  DeleteOutlined,
  EditOutlined,
  SaveOutlined,
  RobotOutlined,
  UnorderedListOutlined,
  DollarOutlined,
  SearchOutlined,
  FileTextOutlined,
  WarningOutlined,
  FlagFilled,
  SettingOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import api from '../../functions/api';
import ThreeTablesModal from '../SearchResultModal.jsx';
import getUserId from '../../functions/getUserId';
import { callLLM, formatFundamentalFieldsForPrompt, formatTablesDataForPrompt } from '../../functions/LLM_analysis_functions';
import { fetchBroadPictureText, broadPicturePromptBlock } from '../../functions/fetchBroadPictureText'; // DAS-2554

dayjs.extend(relativeTime);

const { Text, Title } = Typography;

const ALERT_DOT_COLORS = {
  Accumulate: '#52c41a',
  'Step In': '#52c41a',
  Harvest: '#ff4d4f',
  Monitor: '#faad14',
};

/**
 * Format currency value
 */
const formatCurrency = (value, currency = 'EUR') => {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('en-EU', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Format portfolio position for prompt (helper for formatStockDataForPrompt)
 */
const formatPortfolioPositionForPrompt = (portfolioData) => {
  if (!portfolioData) return '';
  let text = '';
  if (portfolioData.position !== null && portfolioData.position !== undefined) {
    text += `Current Position: ${portfolioData.position.toLocaleString()} pieces\n`;
  }
  if (portfolioData.exposure !== null && portfolioData.exposure !== undefined) {
    text += `Current Exposure: €${Math.round(portfolioData.exposure).toLocaleString()}\n`;
  }
  return text.trim();
};

/**
 * DAS-1835: Helper to format stock data into structured prompt
 * DAS-1841: Added record parameter to include all record fields
 * Match SelectNowDrawer implementation exactly
 */
const formatStockDataForPrompt = (stockData, tablesData, tickerPortfolioData, record = null) => {
  let prompt = '';

  // DAS-1841: Recommendation Context - all fields from the table record
  if (record) {
    prompt += `--- RECOMMENDATION CONTEXT ---\n`;
    prompt += `Ticker: ${record.ticker || 'N/A'}\n`;
    if (record.company_name) prompt += `Company Name: ${record.company_name}\n`;
    if (record.trade) prompt += `Trade Recommendation: ${record.trade}\n`;
    if (record.quantity !== undefined && record.quantity !== null) prompt += `Recommended Quantity: ${record.quantity.toLocaleString()}\n`;
    if (record.current_qty !== undefined && record.current_qty !== null) prompt += `Current Quantity: ${record.current_qty.toLocaleString()}\n`;
    if (record.target_qty !== undefined && record.target_qty !== null) prompt += `Target Quantity: ${record.target_qty.toLocaleString()}\n`;
    if (record.sector) prompt += `Sector: ${record.sector}\n`;
    if (record.ranking !== undefined && record.ranking !== null) prompt += `Composite Score: ${record.ranking.toFixed(3)}\n`;
    if (record.roa_pct !== undefined && record.roa_pct !== null) prompt += `ROA %: ${record.roa_pct.toFixed(2)}%\n`;
    if (record.roa_zscore !== undefined && record.roa_zscore !== null) prompt += `ROA Z-Score: ${record.roa_zscore.toFixed(2)}\n`;
    if (record.pb_ratio !== undefined && record.pb_ratio !== null) prompt += `P/B Ratio: ${record.pb_ratio.toFixed(2)}\n`;
    if (record.pb_zscore !== undefined && record.pb_zscore !== null) prompt += `P/B Z-Score: ${record.pb_zscore.toFixed(2)}\n`;
    
    // Include any other fields that might be in the record
    const otherFields = Object.keys(record).filter(key => 
      !['ticker', 'company_name', 'trade', 'quantity', 'current_qty', 'target_qty', 'sector', 
        'ranking', 'roa_pct', 'roa_zscore', 'pb_ratio', 'pb_zscore', 'exists_in_dashboard', 'llmResult'].includes(key)
    );
    if (otherFields.length > 0) {
      prompt += `\nOther Fields:\n`;
      otherFields.forEach(key => {
        const value = record[key];
        if (value !== undefined && value !== null && value !== '') {
          prompt += `${key}: ${typeof value === 'number' ? value.toFixed(3) : value}\n`;
        }
      });
    }
    prompt += `\n`;
  }

  // Position Context (if portfolio data available)
  if (tickerPortfolioData) {
    prompt += `--- POSITION CONTEXT ---\n${formatPortfolioPositionForPrompt(tickerPortfolioData)}\n\n`;
  }

  // Fundamental Context
  if (stockData?.fields) {
    const fundamentals = formatFundamentalFieldsForPrompt(stockData.fields);
    if (fundamentals) {
      prompt += `--- FUNDAMENTAL CONTEXT ---\n${fundamentals}\n\n`;
    }
  }

  // Other Data Context (tables) - includes DARLING VIEW, FUNDAMENTAL VIEW, TECHNICAL VIEW, etc.
  if (tablesData && tablesData.length > 0) {
    const tables = formatTablesDataForPrompt(tablesData);
    if (tables) {
      prompt += `--- OTHER DATA CONTEXT ---\n${tables}\n\n`;
    }
  }

  return prompt.trim();
};

/**
 * SelectNowResultsTab - Browse and view saved Select Now results
 */
const SelectNowResultsTab = ({ callAction }) => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [searchTerm, setSearchTerm] = useState('');
  
  // Detail drawer state
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  
  // SearchResultModal state (for ticker details)
  const [searchResultModal, setSearchResultModal] = useState({ open: false, ticker: null });
  
  // DAS-1865: LLM Check state
  const [llmCheckLoading, setLlmCheckLoading] = useState({});
  const [llmCheckResults, setLlmCheckResults] = useState({});
  const [batchLLMCheckLoading, setBatchLLMCheckLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  
  // DAS-1869: Ticker search state for recommendations table
  const [tickerSearchValue, setTickerSearchValue] = useState('');
  const [tickerSearchOptions, setTickerSearchOptions] = useState([]);
  const [filteredTicker, setFilteredTicker] = useState(null);
  
  // DAS-1870: Track modified recommendations for swap functionality
  const [modifiedRecommendations, setModifiedRecommendations] = useState(null);
  const [hasUnsavedSwaps, setHasUnsavedSwaps] = useState(false);
  
  // DAS-1863: Access portfolio data from Redux
  const dispatch = useDispatch();
  const portfolioData = useSelector(state => state.portfolio?.data);
  // DAS-1878: Get sector trends from Redux
  const sectorTrendData = useSelector(state => state.portfolio?.data?.Sector_trend || {});
  
  // DAS-2157: Alert dots state
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [selectedAlertForDialog, setSelectedAlertForDialog] = useState(null);
  const [externalAlerts, setExternalAlerts] = useState([]);
  
  // DAS-2157: Fetch Unknown/Favorites scope alerts when recommendations load
  useEffect(() => {
    if (!selectedResult?.recommendations?.length) {
      setExternalAlerts([]);
      return;
    }
    const tickers = selectedResult.recommendations.map(r => r.ticker).filter(Boolean);
    if (!tickers.length) return;
    
    api.post('/backtesting', {
      action: 'alerts_for_tickers',
      payload: { tickers, scopes: ['Unknown', 'Favorites'] }
    })
      .then(res => {
        setExternalAlerts(res.data?.data?.alerts || []);
      })
      .catch(err => {
        console.error('[DAS-2157] External alerts fetch failed:', err?.response?.status, err?.message);
        setExternalAlerts([]);
      });
  }, [selectedResult?.recommendations]);
  
  // DAS-2157: Build alert map combining Portfolio-scoped (from Redux) and external alerts
  // Portfolio scope → color by alert_type; Unknown/Favorites → always green
  // Deduplicate: one dot per unique (ticker, scope, alert_type) combo
  const alertsByTicker = useMemo(() => {
    const map = {};
    
    // Portfolio-scoped alerts from Redux (scope='Portfolio' already filtered by backend)
    const pfAlerts = portfolioData?.pf_alert || [];
    pfAlerts.forEach(a => {
      if (!a.ticker) return;
      const key = a.ticker.toUpperCase();
      if (!map[key]) map[key] = [];
      const dedupeKey = `Portfolio|${a.alert_type}`;
      if (!map[key].some(x => x._dedupeKey === dedupeKey)) {
        map[key].push({ ...a, scope: 'Portfolio', _dedupeKey: dedupeKey });
      }
    });
    
    // External alerts (Unknown/Favorites scope) → always green, one per alert_type
    externalAlerts.forEach(a => {
      if (!a.ticker) return;
      const key = a.ticker.toUpperCase();
      if (!map[key]) map[key] = [];
      const dedupeKey = `${a.scope}|${a.alert_type}`;
      if (!map[key].some(x => x._dedupeKey === dedupeKey)) {
        map[key].push({ ...a, _dedupeKey: dedupeKey });
      }
    });
    
    return map;
  }, [portfolioData?.pf_alert, externalAlerts]);
  const token = localStorage.getItem('token');

  // DAS-1878: Helper to get sector trend color (matches Sector_trends.jsx logic)
  const getSectorTrendColor = (trendDirection) => {
    if (!trendDirection || typeof trendDirection !== 'string') return 'transparent';
    
    const trend = trendDirection.toLowerCase();
    if (trend.includes('strong') && trend.includes('bullish')) return '#DFF2BF'; // Light green
    if (trend.includes('moderate') && trend.includes('bullish')) return '#E6F5D0'; // Lighter green
    if (trend.includes('mild') && trend.includes('bullish')) return '#F0F8F0'; // Very light green
    if (trend.includes('neutral')) return '#F5F5F5'; // Light gray
    if (trend.includes('mild') && trend.includes('bearish')) return '#FFF8E1'; // Light yellow
    if (trend.includes('moderate') && trend.includes('bearish')) return '#FFE6B3'; // Light orange
    if (trend.includes('strong') && trend.includes('bearish')) return '#FFB3B3'; // Light red
    if (trend.includes('bearish')) return '#FEEFB3'; // Fallback for generic bearish
    
    return 'transparent';
  };

  // DAS-1878: Helper to get sector trend data for a given sector name
  const getSectorTrendForSector = (sectorName) => {
    if (!sectorName || !sectorTrendData?.rows || !sectorTrendData?.columns) return null;
    
    // Get column indices
    const columns = sectorTrendData.columns;
    const sectorIndex = columns.indexOf('sector');
    const directionIndex = columns.indexOf('trend_direction');
    const scoreIndex = columns.indexOf('trend_consensus_score');
    const confidenceIndex = columns.indexOf('trend_confidence');
    const explanationIndex = columns.indexOf('explanation');
    const bullishPctIndex = columns.indexOf('bullish_pct');
    const bearishPctIndex = columns.indexOf('bearish_pct');
    
    // Find the row where sector name matches (use column index lookup)
    const sectorRow = sectorTrendData.rows.find(row => {
      if (sectorIndex >= 0 && sectorIndex < row.length) {
        return row[sectorIndex] === sectorName;
      }
      // Fallback: try row[2] if column index lookup fails (matches Sector_trends.jsx)
      return row[2] === sectorName;
    });
    
    if (!sectorRow) return null;
    
    return {
      sector: sectorIndex >= 0 ? sectorRow[sectorIndex] : sectorRow[2],
      trend_direction: directionIndex >= 0 ? sectorRow[directionIndex] : sectorRow[4],
      trend_consensus_score: scoreIndex >= 0 ? sectorRow[scoreIndex] : sectorRow[3],
      trend_confidence: confidenceIndex >= 0 ? sectorRow[confidenceIndex] : sectorRow[5],
      explanation: explanationIndex >= 0 ? sectorRow[explanationIndex] : sectorRow[12],
      bullish_pct: bullishPctIndex >= 0 ? sectorRow[bullishPctIndex] : sectorRow[6],
      bearish_pct: bearishPctIndex >= 0 ? sectorRow[bearishPctIndex] : sectorRow[7],
    };
  };
  
  // DAS-1863: Fetch portfolio data when viewing a result (if not already loaded)
  useEffect(() => {
    if (selectedResult && !portfolioData && token) {
      console.log('[SelectNowResultsTab] Fetching portfolio data for exposure display...');
      dispatch(getPortfolioData(token));
    }
  }, [selectedResult, portfolioData, token, dispatch]);

  // DAS-1863: Helper function to get exposure and position for a ticker
  const getTickerExposureAndPosition = useCallback((ticker) => {
    if (!portfolioData || !ticker) {
      // Debug: log when portfolio not loaded
      if (ticker && (ticker.toUpperCase() === 'AAPL' || ticker.toUpperCase() === 'NVDA')) {
        console.log(`[Exposure Debug] ${ticker}: portfolioData is ${portfolioData ? 'loaded' : 'NULL'}`);
      }
      return { exposure: null, position: null };
    }
    
    const tickerUpper = ticker.toUpperCase();
    
    // First try to find in portfolio array (direct holdings)
    const portfolioItem = portfolioData.portfolio?.find(
      item => item.Ticker && item.Ticker.toUpperCase() === tickerUpper
    );
    
    // Also check exposures array for total exposure
    const exposureItem = portfolioData.exposures?.find(
      exp => exp.ticker && exp.ticker.toUpperCase() === tickerUpper
    );
    
    const exposure = exposureItem?.total_amount_exposure || portfolioItem?.Value_eur || null;
    const position = portfolioItem?.Position || null;
    
    // Debug: log for AAPL/NVDA
    if (tickerUpper === 'AAPL' || tickerUpper === 'NVDA') {
      console.log(`[Exposure Debug] ${tickerUpper}:`, {
        portfolioItem: portfolioItem ? { Ticker: portfolioItem.Ticker, Position: portfolioItem.Position, Value_eur: portfolioItem.Value_eur } : null,
        exposureItem: exposureItem ? { ticker: exposureItem.ticker, total_amount_exposure: exposureItem.total_amount_exposure } : null,
        result: { exposure, position },
        portfolioTickers: portfolioData.portfolio?.slice(0, 5).map(p => p.Ticker),
        exposureTickers: portfolioData.exposures?.slice(0, 5).map(e => e.ticker),
      });
    }
    
    return { exposure, position };
  }, [portfolioData]);

  /**
   * Fetch saved results from backend
   */
  const fetchResults = useCallback(async () => {
    setLoading(true);
    try {
      const response = await callAction('list_select_now_results', {
        limit: pagination.pageSize,
        offset: (pagination.current - 1) * pagination.pageSize,
      }, { useLoader: false });
      
      if (response?.success) {
        setResults(response.results || response.data?.results || []);
        setTotal(response.total || response.data?.total || 0);
      } else {
        message.error(response?.error || response?.data?.error || 'Failed to fetch results');
      }
    } catch (err) {
      message.error(err.message || 'Failed to fetch results');
    } finally {
      setLoading(false);
    }
  }, [callAction, pagination]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  /**
   * Open detail drawer for a result
   */
  const handleViewDetails = async (record) => {
    setDetailLoading(true);
    setDetailDrawerOpen(true);
    setSelectedResult(null);
    // DAS-1865: Reset LLM check state for new result
    setLlmCheckResults({});
    setLlmCheckLoading({});
    // DAS-1869: Reset ticker search state
    setTickerSearchValue('');
    setTickerSearchOptions([]);
    setFilteredTicker(null);
    
    try {
      const response = await callAction('get_select_now_result', {
        result_id: record.id,
      }, { useLoader: false });
      
      // DAS-2344: Hoist emergency_derisk_recommendation from its nested
      // location under summary_json up to a top-level field so the
      // existing JSX (selectedResult.emergency_derisk_recommendation)
      // renders without further changes.
      const _hoist = (r) => {
        if (!r) return r;
        const nested = r.summary && r.summary.emergency_derisk_recommendation;
        if (nested && !r.emergency_derisk_recommendation) {
          return { ...r, emergency_derisk_recommendation: nested };
        }
        return r;
      };
      if (response?.success && response.result) {
        setSelectedResult(_hoist(response.result));
      } else if (response?.data?.success && response.data.result) {
        setSelectedResult(_hoist(response.data.result));
      } else {
        message.error(response?.error || response?.data?.error || 'Failed to fetch result details');
        setDetailDrawerOpen(false);
      }
    } catch (err) {
      message.error(err.message || 'Failed to fetch result details');
      setDetailDrawerOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  /**
   * Delete a result
   */
  const handleDelete = async (record) => {
    try {
      const response = await callAction('delete_select_now_result', {
        result_id: record.id,
      }, { useLoader: false });
      
      if (response?.success || response?.data?.success) {
        message.success('Result deleted successfully');
        fetchResults();
      } else {
        message.error(response?.error || response?.data?.error || 'Failed to delete result');
      }
    } catch (err) {
      message.error(err.message || 'Failed to delete result');
    }
  };

  /**
   * Open edit modal
   */
  const handleEditOpen = () => {
    if (!selectedResult) return;
    setEditName(selectedResult.name || '');
    setEditNotes(selectedResult.notes || '');
    setEditModalOpen(true);
  };

  /**
   * Save edited result
   */
  const handleEditSave = async () => {
    if (!selectedResult) return;
    
    setEditLoading(true);
    try {
      const response = await callAction('update_select_now_result', {
        result_id: selectedResult.id,
        name: editName || null,
        notes: editNotes || null,
      }, { useLoader: false });
      
      if (response?.success || response?.data?.success) {
        message.success('Result updated successfully');
        setEditModalOpen(false);
        // Update local state
        setSelectedResult(prev => ({ ...prev, name: editName, notes: editNotes }));
        fetchResults(); // Refresh list
      } else {
        message.error(response?.error || response?.data?.error || 'Failed to update result');
      }
    } catch (err) {
      message.error(err.message || 'Failed to update result');
    } finally {
      setEditLoading(false);
    }
  };

  // DAS-1865: Extract score from LLM response
  const extractScoreFromResponse = (response) => {
    if (!response) return null;
    const patterns = [
      /(?:score|rating)[\s:]*(\d+(?:\.\d+)?)\s*(?:\/\s*10|out of 10)/i,
      /(\d+(?:\.\d+)?)\s*(?:\/\s*10|out of 10)/i,
      /\b([0-9]|10)(?:\.\d+)?\s*\/\s*10\b/i,
      /(?:fundamental buy|buy rating|score)[\s:]*(\d+(?:\.\d+)?)/i,
    ];
    for (const pattern of patterns) {
      const match = response.match(pattern);
      if (match) {
        const score = parseFloat(match[1]);
        if (!isNaN(score) && score >= 0 && score <= 10) return score;
      }
    }
    return null;
  };

  // DAS-1865: Handle single LLM check
  // Match SelectNowDrawer logic exactly
  const handleLLMCheck = async (record) => {
    const ticker = record.ticker;
    if (!ticker) return;
    
    // If we already have a successful result (with valid score), don't re-run
    // Allow re-checking if score is null (extraction failed)
    if (llmCheckResults[ticker] && llmCheckResults[ticker].score !== null) {
      return;
    }
    
    setLlmCheckLoading(prev => ({ ...prev, [ticker]: true }));
    
    try {
      // DAS-1835: Fetch stock data from /stock_data endpoint (match SelectNowDrawer exactly)
      const stockResponse = await api.get("/stock_data", { params: { symbol: ticker } });
      
      if (stockResponse.status === 404) {
        message.error(`Ticker ${ticker} not found in database`);
        setLlmCheckLoading(prev => ({ ...prev, [ticker]: false }));
        return;
      }

      const stockData = stockResponse.data;
      
      // Build structured prompt (match SelectNowDrawer)
      const tablesData = stockData.tables || [];
      const tickerPortfolioData = getTickerExposureAndPosition(ticker);
      
      // Get fundamentals fields
      const fundamentalsFields = stockData.ticker_fundamentals?.fields || 
        (stockData.ticker_fundamentals && typeof stockData.ticker_fundamentals === 'object' ? 
          Object.keys(stockData.ticker_fundamentals).map(key => ({
            label: key,
            value: stockData.ticker_fundamentals[key],
            quarterTrend: stockData.fundamentals_trend_analysis_data?.[key]?.trend
          })) : []);

      // DAS-1841: Include all record fields in the prompt context
      // Match SelectNowDrawer: first param should be stockData with fields property
      const dataPrompt = formatStockDataForPrompt(
        { fields: fundamentalsFields }, // stockData object with fields
        tablesData,
        tickerPortfolioData,
        record // Pass the full record to include all its fields
      );

      // DAS-1835: Add the specific question
      const regimeBlock = broadPicturePromptBlock(await fetchBroadPictureText()); // DAS-2554
      const fullPrompt = `${dataPrompt}${regimeBlock}

You are an investor expert. Considering the current market regime above AND the stock's fundamentals, on a scale of 0-10, is this stock a buy? Explain your conclusion`;

      // Call LLM (match SelectNowDrawer - use deepseek-chat)
      const llmResponse = await callLLM(fullPrompt, 'deepseek-chat');
      const score = extractScoreFromResponse(llmResponse);
      
      setLlmCheckResults(prev => ({
        ...prev,
        [ticker]: { score, fullResponse: llmResponse }
      }));
      
    } catch (err) {
      console.error(`LLM check failed for ${ticker}:`, err);
      // Store failed result with null score (allows retry)
      setLlmCheckResults(prev => ({
        ...prev,
        [ticker]: { score: null, fullResponse: `Error: ${err.message}` }
      }));
      message.error(`LLM check failed for ${ticker}: ${err.message}`);
    } finally {
      setLlmCheckLoading(prev => ({ ...prev, [ticker]: false }));
    }
  };

  // DAS-1865: Handle batch LLM check for all BUY records
  // Match SelectNowDrawer logic exactly
  const handleLLMCheckAllBuy = async () => {
    if (!selectedResult?.recommendations) return;
    
    // Filter: BUY records that don't have successful LLM results yet
    // DAS-1865: Don't filter by exists_in_dashboard - let the API handle errors if ticker doesn't exist
    // A result is only "complete" if it has a valid score (not null)
    // Check both live state and saved results
    const buyRecords = selectedResult.recommendations.filter(record => {
      if (record.trade !== 'BUY') return false;
      
      // Check live state (same as SelectNowDrawer)
      const liveResult = llmCheckResults[record.ticker];
      if (liveResult && liveResult.score !== null && liveResult.score !== undefined) {
        return false;
      }
      
      // Also check saved result from database
      const savedResult = record.llmResult;
      if (savedResult) {
        // Handle both object format {score, fullResponse} and direct score value
        const savedScore = savedResult.score !== undefined ? savedResult.score : (typeof savedResult === 'object' ? null : savedResult);
        if (savedScore !== null && savedScore !== undefined) {
          return false;
        }
      }
      
      return true;
    });
    
    if (buyRecords.length === 0) {
      message.info('All BUY records already have successful LLM checks');
      return;
    }
    
    // Clear any failed results (score: null) before retrying (same as SelectNowDrawer)
    setLlmCheckResults(prev => {
      const cleaned = { ...prev };
      buyRecords.forEach(record => {
        if (cleaned[record.ticker]?.score === null) {
          delete cleaned[record.ticker];
        }
      });
      return cleaned;
    });
    
    setBatchLLMCheckLoading(true);
    message.loading({ content: `Processing 0/${buyRecords.length} BUY records...`, key: 'batch-llm', duration: 0 });
    
    let successCount = 0;
    let errorCount = 0;
    
    // Process sequentially to avoid overwhelming the API
    for (let i = 0; i < buyRecords.length; i++) {
      const record = buyRecords[i];
      const ticker = record.ticker;
      
      try {
        message.loading({ content: `Processing ${i + 1}/${buyRecords.length} BUY records...`, key: 'batch-llm', duration: 0 });
        await handleLLMCheck(record);
        successCount++;
        // Small delay between calls
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (err) {
        console.error(`[Batch LLM] Failed for ${ticker}:`, err);
        errorCount++;
      }
    }
    
    message.destroy('batch-llm');
    if (errorCount === 0) {
      message.success(`Successfully processed ${successCount} BUY records`);
    } else {
      message.warning(`Processed ${successCount} records, ${errorCount} failed`);
    }
    
    setBatchLLMCheckLoading(false);
  };

  // DAS-1865 & DAS-1870: Save updated results with LLM data, swapped trades, and exposure data
  const handleSaveUpdatedResults = async () => {
    if (!selectedResult) return;
    
    setSaveLoading(true);
    try {
      // Use modified recommendations if swaps were made, otherwise original
      const baseRecommendations = modifiedRecommendations || selectedResult.recommendations || [];
      
      // Merge LLM results AND current exposure data into recommendations
      const updatedRecommendations = baseRecommendations.map(rec => {
        const llmResult = llmCheckResults[rec.ticker];
        // DAS-1870: Try to get live exposure data, fall back to existing saved data
        const { exposure: liveExposure, position: livePosition } = getTickerExposureAndPosition(rec.ticker);
        
        // Prefer live data, but keep existing saved data if live not available
        const exposure = liveExposure ?? rec.saved_exposure ?? null;
        const position = livePosition ?? rec.saved_position ?? null;
        
        return {
          ...rec,
          ...(llmResult && { llmResult }),
          // Save exposure data if available (will be used as fallback after logout)
          // Keep existing saved values if no live data available
          ...(exposure !== null && { saved_exposure: exposure }),
          ...(position !== null && { saved_position: position }),
        };
      });
      
      const response = await callAction('update_select_now_result', {
        result_id: selectedResult.id,
        name: selectedResult.name,
        notes: selectedResult.notes,
        recommendations: updatedRecommendations,
      }, { useLoader: false });
      
      if (response?.success || response?.data?.success) {
        message.success('Results saved' + (hasUnsavedSwaps ? ' with swapped trades' : ''));
        // Update local state
        setSelectedResult(prev => ({ ...prev, recommendations: updatedRecommendations }));
        setModifiedRecommendations(null);
        setHasUnsavedSwaps(false);
      } else {
        message.error(response?.error || 'Failed to save results');
      }
    } catch (err) {
      message.error(err.message || 'Failed to save results');
    } finally {
      setSaveLoading(false);
    }
  };

  // DAS-1870: Handle swap BUY ↔ NEXT within same sector
  const handleSwapTrade = useCallback((buyRecord) => {
    if (!selectedResult?.recommendations) return;
    
    const recommendations = modifiedRecommendations || selectedResult.recommendations;
    const sector = buyRecord.sector;
    const targetValue = selectedResult.full_config?.position_sizing?.new_position_amount_eur || 10000;
    
    // Find the highest-ranked NEXT (RUNNER_UP) record in the same sector
    // Records are already sorted by ranking (composite score), so first match is highest
    const nextRecord = recommendations.find(rec => 
      rec.trade === 'RUNNER_UP' && 
      rec.sector === sector && 
      rec.ticker !== buyRecord.ticker
    );
    
    if (!nextRecord) {
      message.warning(`No NEXT candidate found in sector "${sector}" to swap with`);
      return;
    }
    
    // Calculate quantities based on target value and prices
    const calculateQuantity = (priceEur) => {
      if (!priceEur || priceEur <= 0) return 0;
      return Math.floor(targetValue / priceEur);
    };
    
    // Create updated recommendations with swapped trades
    const updatedRecommendations = recommendations.map(rec => {
      if (rec.ticker === buyRecord.ticker) {
        // BUY → NEXT
        return {
          ...rec,
          trade: 'RUNNER_UP',
          quantity: 0,
          target_qty: 0,
          trade_value_eur: 0,
          transaction_cost_eur: 0,
        };
      }
      if (rec.ticker === nextRecord.ticker) {
        // NEXT → BUY
        const newQty = calculateQuantity(rec.price_eur);
        const tradeValue = newQty * (rec.price_eur || 0);
        return {
          ...rec,
          trade: 'BUY',
          quantity: newQty,
          target_qty: newQty,
          current_qty: rec.current_qty || 0,
          trade_value_eur: tradeValue,
          transaction_cost_eur: tradeValue * 0.001, // Estimate 0.1% cost
        };
      }
      return rec;
    });
    
    setModifiedRecommendations(updatedRecommendations);
    setHasUnsavedSwaps(true);
    message.success(`Swapped: ${buyRecord.ticker} (BUY→NEXT) ↔ ${nextRecord.ticker} (NEXT→BUY)`);
  }, [selectedResult, modifiedRecommendations]);

  // DAS-1870: Reset modified recommendations when opening a new result
  const selectedResultId = selectedResult?.id;
  useEffect(() => {
    if (selectedResultId) {
      setModifiedRecommendations(null);
      setHasUnsavedSwaps(false);
    }
  }, [selectedResultId]);

  /**
   * Table columns
   */
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      render: (id) => <Text code>#{id}</Text>,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (name, record) => (
        <div>
          <Text strong>{name || <Text type="secondary">Untitled</Text>}</Text>
          {record.notes && (
            <Tooltip title={record.notes}>
              <FileTextOutlined style={{ marginLeft: 8, color: '#999' }} />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: 'Run / Config',
      key: 'run_config',
      width: 120,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Tag color="blue">Run #{record.run_id}</Tag>
          {record.configuration_id && (
            <Text type="secondary" style={{ fontSize: 11 }}>Config #{record.configuration_id}</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'As of Date',
      dataIndex: 'as_of_date',
      key: 'as_of_date',
      width: 120,
      render: (date) => date ? dayjs(date).format('YYYY-MM-DD') : '-',
    },
    {
      title: 'Saved',
      dataIndex: 'saved_at',
      key: 'saved_at',
      width: 150,
      render: (date) => date ? (
        <Tooltip title={dayjs(date).format('YYYY-MM-DD HH:mm:ss')}>
          {dayjs(date).fromNow()}
        </Tooltip>
      ) : '-',
    },
    {
      title: 'Factors',
      key: 'factors',
      width: 150,
      render: (_, record) => {
        const factors = record.config_summary?.factors || [];
        return (
          <Space wrap size={[2, 2]}>
            {factors.slice(0, 3).map((f, i) => (
              <Tag key={i} style={{ fontSize: 11 }}>{f}</Tag>
            ))}
            {factors.length > 3 && (
              <Tag style={{ fontSize: 11 }}>+{factors.length - 3}</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Summary',
      key: 'summary',
      width: 150,
      render: (_, record) => {
        const summary = record.summary || {};
        return (
          <Space split="|" size="small">
            <Text style={{ color: '#cf1322' }}>
              {summary.buy_count || 0} Buy
            </Text>
            <Text style={{ color: '#52c41a' }}>
              {summary.sell_count || 0} Sell
            </Text>
            {summary.llm_checked_count > 0 && (
              <Tooltip title={`${summary.llm_checked_count} LLM checks`}>
                <Badge count={summary.llm_checked_count} size="small" style={{ backgroundColor: '#722ed1' }}>
                  <RobotOutlined style={{ color: '#722ed1' }} />
                </Badge>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this result?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  /**
   * Recommendations table columns (for detail drawer)
   * Matches the original SelectNowDrawer table columns
   */
  // DAS-1869 & DAS-1870: Calculate rankings based on Score (sorted descending)
  // This ensures numbering reflects Score ranking even when filtered
  // Use modified recommendations if swaps were made
  const sortedRecommendations = useMemo(() => {
    const recommendations = modifiedRecommendations || selectedResult?.recommendations;
    if (!recommendations) return [];
    return [...recommendations].sort((a, b) => {
      const scoreA = a.ranking || 0;
      const scoreB = b.ranking || 0;
      return scoreB - scoreA; // Descending order (highest first)
    });
  }, [selectedResult?.recommendations, modifiedRecommendations]);

  // Create a map of ticker to rank based on sorted position
  const tickerRankMap = useMemo(() => {
    const rankMap = {};
    sortedRecommendations.forEach((record, index) => {
      const key = `${record.ticker}-${record.trade}`;
      rankMap[key] = index + 1;
    });
    return rankMap;
  }, [sortedRecommendations]);

  const recommendationColumns = [
    {
      title: '#',
      key: 'rowNumber',
      width: 45,
      align: 'center',
      fixed: 'left',
      render: (_, record) => {
        const key = `${record.ticker}-${record.trade}`;
        const rank = tickerRankMap[key] || '-';
        return (
          <Text type="secondary" style={{ fontWeight: 'bold' }}>{rank}</Text>
        );
      },
    },
    {
      title: 'Ticker / Company',
      dataIndex: 'ticker',
      key: 'ticker',
      width: 160,
      fixed: 'left',
      render: (ticker, record) => {
        const tickerAlerts = alertsByTicker[ticker?.toUpperCase()] || [];
        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {tickerAlerts.map((alert, idx) => {
                const isPortfolioScope = alert.scope === 'Portfolio';
                const dotColor = isPortfolioScope
                  ? (ALERT_DOT_COLORS[alert.alert_type] || '#999')
                  : '#52c41a';
                const scopeLabel = isPortfolioScope ? '' : ` [${alert.scope || ''}]`;
                const tipText = `${alert.alert_type || ''}${alert.alert_subtype ? ' — ' + alert.alert_subtype : ''}${scopeLabel}: ${alert.pf_alert_text || 'Alert'}`;
                return (
                  <Tooltip
                    key={alert._dedupeKey || alert.ID || idx}
                    title={tipText}
                  >
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isPortfolioScope) {
                          setSelectedAlertForDialog(alert);
                          setAlertDialogOpen(true);
                        }
                      }}
                      style={{
                        display: 'inline-block',
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: dotColor,
                        cursor: isPortfolioScope ? 'pointer' : 'default',
                        flexShrink: 0,
                      }}
                    />
                  </Tooltip>
                );
              })}
              <Tooltip title="Click for details">
                <span
                  style={{
                    cursor: 'pointer',
                    color: '#1890ff',
                    fontWeight: 'bold',
                    textDecoration: 'underline',
                  }}
                  onClick={() => setSearchResultModal({ open: true, ticker })}
                >
                  {ticker}
                </span>
              </Tooltip>
              {record.likely_institutional_entry && (
                <Tooltip title={record.institutional_trend_text || 'Likely Institutional Entry'}>
                  <WarningOutlined style={{ color: '#52c41a', fontSize: 12, marginLeft: 2 }} />
                </Tooltip>
              )}
              {record.likely_institutional_exit && (
                <Tooltip title={record.institutional_trend_text || 'Likely Institutional Exit'}>
                  <WarningOutlined style={{ color: '#ff4d4f', fontSize: 12, marginLeft: 2 }} />
                </Tooltip>
              )}
            </div>
            {record.company_name && record.company_name !== ticker && (
              <div style={{ fontSize: '11px', color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '140px' }}>
                {record.company_name}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Trade',
      dataIndex: 'trade',
      key: 'trade',
      width: 110,
      render: (trade, record) => {
        const color = trade === 'BUY' ? 'red' : trade === 'SELL' ? 'green' : trade === 'HOLD' ? 'blue' : 'default';
        const label = trade === 'RUNNER_UP' ? 'NEXT' : trade;
        
        // DAS-1870: Show swap button for BUY records
        if (trade === 'BUY') {
          return (
            <Space size={2}>
              <Tag color={color}>{label}</Tag>
              <Tooltip title={`Swap with highest NEXT in ${record.sector || 'same sector'}`}>
                <Button
                  type="link"
                  size="small"
                  onClick={() => handleSwapTrade(record)}
                  style={{ padding: '0 2px', fontSize: '10px', height: 'auto' }}
                >
                  ↔
                </Button>
              </Tooltip>
            </Space>
          );
        }
        
        return <Tag color={color}>{label}</Tag>;
      },
    },
    // DAS-1863 & DAS-1870: Exposure column showing current exposure in EUR and position in pieces
    // Priority: live data > saved data > calculated from current_qty
    {
      title: (
        <Tooltip title="Current portfolio exposure. Shows live data when available, otherwise saved data.">
          <span>Exposure</span>
        </Tooltip>
      ),
      key: 'exposure',
      width: 130,
      align: 'right',
      render: (_, record) => {
        // Try live data first
        const { exposure: liveExposure, position: livePosition } = getTickerExposureAndPosition(record.ticker);
        
        // Check if data is saved
        const hasSavedData = record.saved_exposure !== null && record.saved_exposure !== undefined;
        
        // Fall back to saved data, then to calculated from current_qty
        let exposure = liveExposure ?? record.saved_exposure ?? null;
        let position = livePosition ?? record.saved_position ?? null;
        
        // If still no data, calculate from current_qty (the position at time of Select Now)
        if (position === null && record.current_qty !== undefined && record.current_qty !== null && record.current_qty > 0) {
          position = record.current_qty;
        }
        if (exposure === null && position !== null && position > 0 && record.price_eur) {
          exposure = position * record.price_eur;
        }
        
        // Check if we have live data that could be saved
        const hasLiveData = liveExposure !== null || livePosition !== null;
        const needsSave = hasLiveData && !hasSavedData;
        
        if ((exposure === null || exposure === 0) && (position === null || position === 0)) {
          // Show hint if live data available but not saved
          if (needsSave) {
            return (
              <Tooltip title="Live data available - click Save to persist">
                <Text style={{ fontSize: '12px', color: '#1890ff' }}>
                  €{Math.round(liveExposure || 0).toLocaleString()} / {Math.round(livePosition || 0).toLocaleString()}
                  <span style={{ marginLeft: 2, fontSize: '9px' }}>⚡</span>
                </Text>
              </Tooltip>
            );
          }
          return <Text type="secondary">-</Text>;
        }
        
        const exposureStr = exposure !== null && exposure > 0 ? `€${Math.round(exposure).toLocaleString()}` : '€-';
        const positionStr = position !== null && position > 0 ? `${Math.round(position).toLocaleString()}` : '-';
        
        // Show indicator based on data source
        let icon = '';
        let tooltipText = '';
        let color = 'inherit';
        
        if (hasLiveData) {
          icon = needsSave ? '⚡' : '✓';
          tooltipText = needsSave ? 'Live data (click Save to persist)' : 'Live portfolio data (saved)';
          color = needsSave ? '#1890ff' : 'inherit';
        } else if (hasSavedData) {
          icon = '💾';
          tooltipText = 'From saved data';
          color = '#888';
        } else {
          icon = '📋';
          tooltipText = 'Calculated from recommendation';
          color = '#888';
        }
        
        return (
          <Tooltip title={tooltipText}>
            <Text style={{ fontSize: '12px', color }}>
              {exposureStr} / {positionStr}
              {icon && <span style={{ marginLeft: 2, fontSize: '9px' }}>{icon}</span>}
            </Text>
          </Tooltip>
        );
      },
    },
    {
      title: (
        <Space direction="vertical" size={0} style={{ width: '100%' }}>
          <span>LLM Check</span>
          <Button
            type="link"
            size="small"
            icon={<RobotOutlined />}
            onClick={handleLLMCheckAllBuy}
            disabled={batchLLMCheckLoading || !selectedResult?.recommendations}
            loading={batchLLMCheckLoading}
            style={{ padding: '0 4px', height: 'auto', fontSize: '11px', lineHeight: '1.2' }}
          >
            Check all Buy
          </Button>
        </Space>
      ),
      key: 'llm_check',
      width: 120,
      align: 'center',
      render: (_, record) => {
        // DAS-1865: Show LLM Check for ALL records - let API handle errors if ticker doesn't exist
        // Don't filter by exists_in_dashboard - user wants to be able to check all tickers
        
        const isLoading = llmCheckLoading[record.ticker];
        // DAS-1865: Check both live state and saved results (match SelectNowDrawer pattern)
        const liveResult = llmCheckResults[record.ticker];
        const savedResult = record.llmResult;
        
        // Prefer live result, fall back to saved result
        const result = liveResult || savedResult;
        
        // DAS-1835: Show score if we have a result (even if score extraction failed) - match SelectNowDrawer
        if (result) {
          // If score is null, extraction failed but we still have a response
          const scoreValue = result.score !== null && result.score !== undefined ? result.score : '?';
          // Color code the score: 0-3 red, 4-6 yellow, 7-10 green, gray for unknown
          let color = '#888'; // gray for unknown
          if (result.score !== null && result.score !== undefined) {
            color = '#cf1322'; // red (default)
            if (result.score >= 7) color = '#52c41a'; // green
            else if (result.score >= 4) color = '#faad14'; // yellow
          }
          
          return (
            <Tooltip 
              title={
                <div style={{ maxWidth: 500, maxHeight: 400, overflow: 'auto', padding: 8 }}>
                  <div style={{ fontWeight: 'bold', marginBottom: 8 }}>LLM Analysis - {record.ticker}</div>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{result.fullResponse || 'Analysis available'}</div>
                </div>
              }
              placement="left"
            >
              <div style={{ fontSize: '18px', fontWeight: 'bold', color, cursor: 'help' }}>
                {scoreValue}/10
              </div>
            </Tooltip>
          );
        }
        
        // Show button if no result exists (match SelectNowDrawer)
        return (
          <Button
            type="primary"
            size="small"
            icon={isLoading ? <LoadingOutlined spin /> : <RobotOutlined />}
            onClick={() => handleLLMCheck(record)}
            disabled={isLoading}
            loading={isLoading}
          >
            {isLoading ? '...' : 'Check'}
          </Button>
        );
      },
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 70,
      align: 'right',
      render: (qty, record) => record.trade === 'HOLD' ? '-' : <Text strong>{qty?.toLocaleString() || 0}</Text>,
    },
    {
      title: 'Current→Target',
      key: 'qty_change',
      width: 100,
      align: 'center',
      render: (_, record) => {
        const current = record.current_qty || 0;
        const target = record.target_qty || 0;
        const net = target - current;
        
        return (
          <div style={{ textAlign: 'center' }}>
            <Space size="small">
              <Text type="secondary">{current}</Text>
              <Text type="secondary">→</Text>
              <Text strong>{target}</Text>
            </Space>
            {/* DAS-1917: Show net buy/sell underneath */}
            {net !== 0 && record.trade !== 'RUNNER_UP' && (
              <div style={{ fontSize: '11px', marginTop: '2px' }}>
                <Text type={net > 0 ? 'danger' : 'success'} style={{ fontSize: '11px' }}>
                  {net > 0 ? `buy net ${net}` : `sell net ${Math.abs(net)}`}
                </Text>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Sector',
      dataIndex: 'sector',
      key: 'sector',
      width: 120,
      ellipsis: true,
      // DAS-1878: Show sector trend with coloring and tooltip
      render: (sector, record) => {
        if (!sector) return '-';
        
        const trendData = getSectorTrendForSector(sector);
        const backgroundColor = trendData ? getSectorTrendColor(trendData.trend_direction) : 'transparent';
        
        // Build tooltip content
        let tooltipContent = `Sector: ${sector}`;
        if (trendData) {
          tooltipContent += `\n\nTrend Direction: ${trendData.trend_direction || 'N/A'}`;
          if (trendData.trend_consensus_score != null) {
            tooltipContent += `\nConsensus Score: ${trendData.trend_consensus_score}`;
          }
          if (trendData.trend_confidence) {
            tooltipContent += `\nConfidence: ${trendData.trend_confidence}`;
          }
          if (trendData.bullish_pct != null && trendData.bearish_pct != null) {
            tooltipContent += `\nBullish: ${trendData.bullish_pct}% | Bearish: ${trendData.bearish_pct}%`;
          }
          if (trendData.explanation) {
            tooltipContent += `\n\nExplanation:\n${trendData.explanation}`;
          }
        } else {
          tooltipContent += '\n\nNo sector trend data available';
        }
        
        return (
          <Tooltip title={tooltipContent} placement="top">
            <div
              style={{
                backgroundColor,
                padding: '2px 4px',
                borderRadius: '2px',
                cursor: 'help',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {sector}
            </div>
          </Tooltip>
        );
      },
    },
    // DAS-1865: Fund Trend column - EXACT MATCH with SelectNowDrawer
    {
      title: (
        <Tooltip title="Fundamental trend analysis: Improvement/Deterioration based on EPS, Revenue, Margins, ROE, FCF trends">
          <span style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>Fund Trend</span>
        </Tooltip>
      ),
      dataIndex: 'fundamental_trend',
      key: 'fundamental_trend',
      width: 110,
      align: 'center',
      render: (val) => {
        if (!val) return '-';
        // Match original getTrendColor logic
        const t = val.toLowerCase();
        let tagColor = 'orange';
        if (t.includes('improvement') || t.includes('positive')) tagColor = 'green';
        else if (t.includes('deterioration') || t.includes('negative')) tagColor = 'red';
        return (
          <Tag color={tagColor} style={{ fontSize: '10px' }}>
            {val}
          </Tag>
        );
      },
    },
    // DAS-1865: Consensus column - EXACT MATCH with SelectNowDrawer
    {
      title: (
        <Tooltip title="Trend consensus score (0-100) combining technical, fundamental, sentiment, and institutional signals">
          <span style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>Consensus</span>
        </Tooltip>
      ),
      dataIndex: 'consensus_score',
      key: 'consensus_score',
      width: 100,
      align: 'center',
      sorter: (a, b) => (a.consensus_score || 0) - (b.consensus_score || 0),
      render: (val, record) => {
        if (val == null) return '-';
        const direction = record.consensus_direction || '';
        const strength = record.consensus_strength || '';
        let color = '#888';
        if (val >= 60) color = '#52c41a';
        else if (val <= 40) color = '#cf1322';
        else color = '#faad14';
        return (
          <Tooltip title={`Direction: ${direction}, Strength: ${strength}`}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '13px', fontWeight: 'bold', color }}>{val.toFixed(0)}</div>
              <div style={{ fontSize: '9px', color: '#666' }}>{strength || '-'}</div>
            </div>
          </Tooltip>
        );
      },
    },
    // DAS-1963: Return Probability column
    // DAS-2327: tooltips routed through shared helper
    {
      title: (
        <Tooltip title={<ReturnProbabilityHeaderTooltip />} overlayStyle={{ maxWidth: 360 }}>
          <span style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>Ret Prob</span>
        </Tooltip>
      ),
      dataIndex: 'return_probability',
      key: 'return_probability',
      width: 75,
      align: 'center',
      sorter: (a, b) => (a.return_probability || 0) - (b.return_probability || 0),
      render: (val, record) => {
        if (val == null) return '-';
        const color = val >= 55 ? '#52c41a' : val >= 45 ? '#faad14' : '#cf1322';
        return (
          <Tooltip title={<ReturnProbabilityTooltip record={record} />} overlayStyle={{ maxWidth: 360 }}>
            <div style={{ fontSize: '12px', fontWeight: 'bold', color, cursor: 'help' }}>{val.toFixed(1)}%</div>
          </Tooltip>
        );
      },
    },
    // DAS-1963: Expected Q Return column
    // DAS-2327: tooltips routed through shared helper
    {
      title: (
        <Tooltip title={<ExpQReturnHeaderTooltip />} overlayStyle={{ maxWidth: 360 }}>
          <span style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>Exp Q</span>
        </Tooltip>
      ),
      dataIndex: 'expected_q_return',
      key: 'expected_q_return',
      width: 65,
      align: 'center',
      sorter: (a, b) => (a.expected_q_return || 0) - (b.expected_q_return || 0),
      render: (val, record) => {
        if (val == null) return '-';
        const color = val >= 0 ? '#52c41a' : '#cf1322';
        return (
          <Tooltip title={<ExpQReturnTooltip record={record} />} overlayStyle={{ maxWidth: 360 }}>
            <div style={{ fontSize: '12px', fontWeight: 'bold', color, cursor: 'help' }}>
              {val >= 0 ? '+' : ''}{val.toFixed(1)}%
            </div>
          </Tooltip>
        );
      },
    },
    // DAS-1982: Reward ÷ Risk column
    // DAS-2327: tooltips routed through shared helper
    {
      title: (
        <Tooltip title={<RewardRiskHeaderTooltip />} overlayStyle={{ maxWidth: 360 }}>
          <span style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>Reward<br/>÷ Risk</span>
        </Tooltip>
      ),
      key: 'risk_adj_q',
      width: 75,
      align: 'center',
      sorter: (a, b) => (a.risk_adj_q_return || 0) - (b.risk_adj_q_return || 0),
      render: (_, record) => {
        const riskAdjQ = record.risk_adj_q_return;
        if (riskAdjQ === null || riskAdjQ === undefined) {
          return <span style={{ color: '#999', fontSize: '11px' }}>N/A</span>;
        }
        const color = riskAdjQ >= 0.5 ? '#389e0d' :
                      riskAdjQ >= 0.3 ? '#52c41a' :
                      riskAdjQ >= 0.1 ? '#fa8c16' :
                      riskAdjQ >= 0 ? '#999' :
                      '#ff4d4f';
        return (
          <Tooltip title={<RewardRiskTooltip record={record} />} overlayStyle={{ maxWidth: 360 }}>
            <span style={{ color, fontWeight: 'bold', cursor: 'help', fontSize: '12px' }}>
              {riskAdjQ.toFixed(2)}
            </span>
          </Tooltip>
        );
      },
    },
    // DAS-2212: Zacks column (matches Portfolio View rendering)
    {
      title: 'Zacks',
      key: 'zacks',
      width: 100,
      render: (_, record) => {
        const { zacks_ranking, zacks_growth, zacks_value, zacks_vgm } = record;
        if (!zacks_ranking && !zacks_growth && !zacks_value && !zacks_vgm) {
          return <span style={{ color: '#999' }}>N/A</span>;
        }
        const getRankColor = (rank) => {
          if (!rank) return '#999';
          if (rank <= 2) return '#52c41a';
          if (rank === 3) return '#faad14';
          return '#ff4d4f';
        };
        const getGradeColor = (grade) => {
          if (!grade) return '#999';
          const g = grade.toUpperCase();
          if (g === 'A' || g === 'B') return '#52c41a';
          if (g === 'C') return '#faad14';
          return '#ff4d4f';
        };
        return (
          <Tooltip title={`Rank: ${zacks_ranking || 'N/A'} | Growth: ${zacks_growth || 'N/A'} | Value: ${zacks_value || 'N/A'} | VGM: ${zacks_vgm || 'N/A'}`}>
            <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
              {zacks_ranking && (
                <span style={{
                  backgroundColor: getRankColor(zacks_ranking),
                  color: 'white',
                  padding: '1px 5px',
                  borderRadius: '3px',
                  fontSize: '11px',
                  fontWeight: 'bold'
                }}>
                  {zacks_ranking}
                </span>
              )}
              {zacks_growth && (
                <span style={{ color: getGradeColor(zacks_growth), fontSize: '11px', fontWeight: 'bold' }}>
                  G:{zacks_growth}
                </span>
              )}
              {zacks_value && (
                <span style={{ color: getGradeColor(zacks_value), fontSize: '11px', fontWeight: 'bold' }}>
                  V:{zacks_value}
                </span>
              )}
              {zacks_vgm && (
                <span style={{ color: getGradeColor(zacks_vgm), fontSize: '11px', fontWeight: 'bold' }}>
                  VGM:{zacks_vgm}
                </span>
              )}
            </div>
          </Tooltip>
        );
      },
    },
    // DAS-1865: ESG column - EXACT MATCH with SelectNowDrawer
    {
      title: (
        <Tooltip title="ESG Sustainability Scores: SA=Sustainalytics Risk Rating (lower is better, 0-40+), MSCI=Letter Rating (AAA-CCC), Refinitiv=Score (0-100, higher is better)">
          <span style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>ESG</span>
        </Tooltip>
      ),
      key: 'esg',
      width: 110,
      align: 'center',
      render: (_, record) => {
        const saRisk = record.esg_sa_risk_rating;
        const msci = record.esg_msci_rating;
        const refinitiv = record.esg_refinitiv_score;
        
        // No ESG data at all
        if (saRisk == null && !msci && refinitiv == null) return '-';
        
        // Color coding for SA Risk (lower is better: <10 green, 10-20 yellow, >20 orange, >30 red)
        const getSaColor = (v) => {
          if (v == null) return '#888';
          if (v < 10) return '#52c41a';
          if (v < 20) return '#a0d911';
          if (v < 30) return '#faad14';
          return '#cf1322';
        };
        
        // Color coding for MSCI rating
        const getMsciColor = (rating) => {
          if (!rating) return '#888';
          if (rating === 'AAA' || rating === 'AA') return '#52c41a';
          if (rating === 'A' || rating === 'BBB') return '#a0d911';
          if (rating === 'BB') return '#faad14';
          return '#cf1322';
        };
        
        // Color coding for Refinitiv (higher is better: >70 green, 50-70 yellow, <50 red)
        const getRefColor = (v) => {
          if (v == null) return '#888';
          if (v >= 70) return '#52c41a';
          if (v >= 50) return '#a0d911';
          if (v >= 30) return '#faad14';
          return '#cf1322';
        };
        
        return (
          <Tooltip 
            title={
              <div style={{ fontSize: 11 }}>
                <div><strong>Sustainalytics Risk:</strong> {saRisk != null ? saRisk.toFixed(1) : 'N/A'} (lower is better)</div>
                <div><strong>MSCI Rating:</strong> {msci || 'N/A'}</div>
                <div><strong>Refinitiv Score:</strong> {refinitiv != null ? refinitiv : 'N/A'}/100 (higher is better)</div>
              </div>
            }
          >
            <div style={{ display: 'flex', gap: '2px', justifyContent: 'center', alignItems: 'center' }}>
              {/* Sustainalytics Risk (small box) */}
              <span style={{ 
                fontSize: '9px', 
                padding: '1px 3px', 
                borderRadius: '2px', 
                background: getSaColor(saRisk),
                color: '#fff',
                minWidth: '24px',
                textAlign: 'center'
              }}>
                {saRisk != null ? saRisk.toFixed(0) : '-'}
              </span>
              {/* MSCI Rating */}
              <span style={{ 
                fontSize: '9px', 
                padding: '1px 3px', 
                borderRadius: '2px', 
                background: getMsciColor(msci),
                color: '#fff',
                minWidth: '26px',
                textAlign: 'center'
              }}>
                {msci || '-'}
              </span>
              {/* Refinitiv Score */}
              <span style={{ 
                fontSize: '9px', 
                padding: '1px 3px', 
                borderRadius: '2px', 
                background: getRefColor(refinitiv),
                color: '#fff',
                minWidth: '22px',
                textAlign: 'center'
              }}>
                {refinitiv != null ? refinitiv : '-'}
              </span>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'ROA %',
      dataIndex: 'roa_pct',
      key: 'roa_pct',
      width: 85,
      align: 'right',
      render: (val, record) => {
        // DAS-1865: Check for validation flags on ROA
        const roaFlags = (record.validation_flags || []).filter(f => f.field === 'roa');
        const hasRoaFlag = roaFlags.length > 0;
        return (
          <div>
            <Space size={4}>
              {hasRoaFlag && (
                <Tooltip title={
                  <div>
                    <strong>🚩 Data Quality Flag</strong>
                    {roaFlags.map((f, i) => (
                      <div key={i} style={{ marginTop: 4 }}>{f.message || f.issue || 'Validation issue'}</div>
                    ))}
                  </div>
                }>
                  <FlagFilled style={{ color: '#cf1322', fontSize: '12px' }} />
                </Tooltip>
              )}
              {val != null ? (
                <Text style={{ color: val > 0 ? '#52c41a' : '#cf1322' }}>{val.toFixed(1)}%</Text>
              ) : '-'}
            </Space>
            {record.dashboard_roa != null && (
              <div style={{ fontSize: '10px', color: '#999' }}>DB: {record.dashboard_roa.toFixed(1)}%</div>
            )}
          </div>
        );
      },
    },
    {
      title: 'ROA Z',
      dataIndex: 'roa_zscore',
      key: 'roa_zscore',
      width: 70,
      align: 'right',
      render: (val, record) => (
        <div>
          {val != null ? (
            <Text style={{ color: val > 0 ? '#52c41a' : '#cf1322', fontWeight: 'bold' }}>{val.toFixed(2)}</Text>
          ) : '-'}
          {record.dashboard_roa_zscore != null && (
            <div style={{ fontSize: '10px', color: '#999' }}>DB: {record.dashboard_roa_zscore.toFixed(2)}</div>
          )}
        </div>
      ),
    },
    {
      title: 'P/B',
      dataIndex: 'pb_ratio',
      key: 'pb_ratio',
      width: 75,
      align: 'right',
      render: (val, record) => {
        // DAS-1865: Check for validation flags on P/B
        const pbFlags = (record.validation_flags || []).filter(f => f.field === 'priceToBook');
        const hasPbFlag = pbFlags.length > 0;
        return (
          <div>
            <Space size={4}>
              {hasPbFlag && (
                <Tooltip title={
                  <div>
                    <strong>🚩 Data Quality Flag</strong>
                    {pbFlags.map((f, i) => (
                      <div key={i} style={{ marginTop: 4 }}>{f.message || f.issue || 'Validation issue'}</div>
                    ))}
                  </div>
                }>
                  <FlagFilled style={{ color: '#cf1322', fontSize: '12px' }} />
                </Tooltip>
              )}
              {val != null ? <Text>{val.toFixed(2)}</Text> : '-'}
            </Space>
            {record.dashboard_pb != null && (
              <div style={{ fontSize: '10px', color: '#999' }}>DB: {record.dashboard_pb.toFixed(2)}</div>
            )}
          </div>
        );
      },
    },
    {
      title: 'P/B Z',
      dataIndex: 'pb_zscore',
      key: 'pb_zscore',
      width: 70,
      align: 'right',
      render: (val, record) => (
        <div>
          {val != null ? (
            <Text style={{ color: val < 0 ? '#52c41a' : '#cf1322', fontWeight: 'bold' }}>{val.toFixed(2)}</Text>
          ) : '-'}
          {record.dashboard_pb_zscore != null && (
            <div style={{ fontSize: '10px', color: '#999' }}>DB: {record.dashboard_pb_zscore.toFixed(2)}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Score',
      dataIndex: 'ranking',
      key: 'ranking',
      width: 75,
      align: 'right',
      sorter: (a, b) => (a.ranking || 0) - (b.ranking || 0),
      defaultSortOrder: 'descend',
      render: (val, record) => (
        <div>
          {val != null ? (
            <Text strong style={{ color: val > 0 ? '#52c41a' : '#cf1322' }}>{val.toFixed(3)}</Text>
          ) : '-'}
          {record.dashboard_score != null && (
            <div style={{ fontSize: '10px', color: '#999' }}>DB: {record.dashboard_score.toFixed(3)}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Mom %',
      dataIndex: 'momentum_pct',
      key: 'momentum_pct',
      width: 65,
      align: 'right',
      render: (val) => val != null ? (
        <Text style={{ color: val > 0 ? '#52c41a' : '#cf1322' }}>{val.toFixed(1)}%</Text>
      ) : '-',
    },
    {
      title: 'Mkt Cap',
      dataIndex: 'market_cap',
      key: 'market_cap',
      width: 80,
      align: 'right',
      render: (val, record) => (
        <div>
          {val || '-'}
          {record.yf_market_cap && (
            <div style={{ fontSize: '10px', color: '#999' }}>YF: {record.yf_market_cap}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Ccy',
      dataIndex: 'currency',
      key: 'currency',
      width: 50,
      align: 'center',
      render: (ccy) => <Text type="secondary">{ccy || '-'}</Text>,
    },
    {
      title: 'Price €',
      dataIndex: 'price_eur',
      key: 'price_eur',
      width: 75,
      align: 'right',
      render: (val) => val != null ? `€${val.toFixed(2)}` : '-',
    },
    {
      title: 'Value €',
      dataIndex: 'trade_value_eur',
      key: 'trade_value_eur',
      width: 85,
      align: 'right',
      render: (val) => val != null ? formatCurrency(val) : '-',
    },
    {
      title: 'Cost €',
      dataIndex: 'transaction_cost_eur',
      key: 'transaction_cost_eur',
      width: 70,
      align: 'right',
      render: (val) => val != null ? `€${val.toFixed(2)}` : '-',
    },
  ];

  // Filter results by search term
  const filteredResults = searchTerm
    ? results.filter(r => 
        (r.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.notes || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        `#${r.id}`.includes(searchTerm) ||
        `#${r.run_id}`.includes(searchTerm)
      )
    : results;

  return (
    <div style={{ padding: '16px 0' }}>
      {/* Header */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={5} style={{ margin: 0 }}>
            <SaveOutlined style={{ marginRight: 8 }} />
            Saved Select Now Results
          </Title>
          <Text type="secondary">
            {total} saved result{total !== 1 ? 's' : ''}
          </Text>
        </div>
        <Space>
          <Input
            placeholder="Search by name, notes, ID..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: 250 }}
            allowClear
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchResults}
            loading={loading}
          >
            Refresh
          </Button>
        </Space>
      </div>

      {/* Results Table */}
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={filteredResults}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: total,
          showSizeChanger: true,
          showTotal: (total) => `${total} results`,
          onChange: (page, pageSize) => setPagination({ current: page, pageSize }),
        }}
        size="small"
        scroll={{ x: 1100 }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span>
                  No saved Select Now results yet.
                  <br />
                  <Text type="secondary">
                    Use the "Save Result" button in the Select Now drawer to save your selections.
                  </Text>
                </span>
              }
            />
          ),
        }}
      />

      {/* Detail Drawer */}
      <Drawer
        title={
          <Space>
            <DollarOutlined />
            <span>
              {selectedResult?.name || `Select Now Result #${selectedResult?.id || ''}`}
            </span>
            {selectedResult?.run_id && (
              <Tag color="blue">Run #{selectedResult.run_id}</Tag>
            )}
            {selectedResult?.configuration_id && (
              <Tag color="cyan">Config #{selectedResult.configuration_id}</Tag>
            )}
          </Space>
        }
        placement="right"
        width="80vw"
        open={detailDrawerOpen}
        onClose={() => {
          setDetailDrawerOpen(false);
          setSelectedResult(null);
        }}
        extra={
          <Space>
            {/* DAS-1865 & DAS-1870: Save button for updated LLM results and swapped trades */}
            <Badge dot={hasUnsavedSwaps} offset={[-5, 5]}>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSaveUpdatedResults}
                loading={saveLoading}
                disabled={!selectedResult || (Object.keys(llmCheckResults).length === 0 && !hasUnsavedSwaps)}
              >
                Save{hasUnsavedSwaps ? ' (swaps)' : ''}
              </Button>
            </Badge>
            <Button
              icon={<EditOutlined />}
              onClick={handleEditOpen}
              disabled={!selectedResult}
            >
              Edit
            </Button>
          </Space>
        }
      >
        {detailLoading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" tip="Loading result details..." />
          </div>
        ) : selectedResult ? (
          <div>
            {/* Date Shift Warning - DAS-1876: Enhanced with collapsible detailed explanation */}
            {selectedResult.date_validation?.was_shifted && (() => {
              const selectedDate = selectedResult.date_validation.selected_date;
              const originalDate = selectedResult.date_validation.original_date;
              const daysShifted = selectedResult.date_validation.days_shifted || 0;
              const rules = selectedResult.date_validation.validation_rules;
              const datesChecked = selectedResult.date_validation.dates_checked || [];
              const fieldCoverage = selectedResult.date_validation.field_coverage;
              
              return (
                <Alert
                  message={
                    <Space>
                      <WarningOutlined />
                      <strong>Data Date Shifted</strong>
                      <Text type="secondary">
                        Using data from <strong>{selectedDate}</strong> 
                        {' '}(shifted {daysShifted} day(s) from {originalDate})
                      </Text>
                    </Space>
                  }
                  description={
                    <div>
                      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                        Coverage: Overall {selectedResult.date_validation.coverage?.overall?.pct || 0}% ({selectedResult.date_validation.coverage?.overall?.actual}/{selectedResult.date_validation.coverage?.overall?.expected}), 
                        US {selectedResult.date_validation.coverage?.us?.pct || 0}% ({selectedResult.date_validation.coverage?.us?.actual}/{selectedResult.date_validation.coverage?.us?.expected}), 
                        EU {selectedResult.date_validation.coverage?.eu?.pct || 0}% ({selectedResult.date_validation.coverage?.eu?.actual}/{selectedResult.date_validation.coverage?.eu?.expected}), 
                        Other {selectedResult.date_validation.coverage?.other?.pct || 0}% ({selectedResult.date_validation.coverage?.other?.actual}/{selectedResult.date_validation.coverage?.other?.expected})
                      </Text>
                      
                      {/* DAS-1876: Collapsible detailed explanation */}
                      {(rules || datesChecked.length > 0 || fieldCoverage) && (
                        <Collapse 
                          ghost 
                          size="small"
                          style={{ marginTop: 8, background: 'transparent' }}
                          items={[{
                            key: 'details',
                            label: <Text type="secondary" style={{ fontSize: 12 }}>📋 Show detailed validation explanation</Text>,
                            children: (
                              <div style={{ fontSize: 12 }}>
                                {/* Validation Rules */}
                                {rules && (
                                  <div style={{ marginBottom: 12 }}>
                                    <Text strong style={{ display: 'block', marginBottom: 4 }}>📏 Validation Rules:</Text>
                                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                                      <li><Text type="secondary">Coverage threshold: <strong>{rules.coverage_threshold_pct}%</strong> minimum for each region</Text></li>
                                      <li><Text type="secondary">Max lookback: <strong>{rules.max_lookback_days} days</strong></Text></li>
                                      <li><Text type="secondary">Required fields: <strong>{rules.required_fields?.join(', ')}</strong></Text></li>
                                      <li><Text type="secondary">Field conditions: {rules.required_field_conditions}</Text></li>
                                      <li><Text type="secondary">Region rule: {rules.region_rule}</Text></li>
                                      <li><Text type="secondary">Data source: <code>{rules.data_source}</code></Text></li>
                                    </ul>
                                  </div>
                                )}
                                
                                {/* Field-level coverage issues */}
                                {fieldCoverage && (
                                  <div style={{ marginBottom: 12 }}>
                                    <Text strong style={{ display: 'block', marginBottom: 4 }}>🔍 Field-Level Issues on {selectedDate}:</Text>
                                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                                      <li>
                                        <Text type="secondary">
                                          Missing ROA: <strong>{fieldCoverage.missing_roa?.total || 0}</strong> tickers
                                          {' '}(US: {fieldCoverage.missing_roa?.us || 0}, EU: {fieldCoverage.missing_roa?.eu || 0}, Other: {fieldCoverage.missing_roa?.other || 0})
                                        </Text>
                                      </li>
                                      <li>
                                        <Text type="secondary">
                                          Missing P/B: <strong>{fieldCoverage.missing_pb?.total || 0}</strong> tickers
                                          {' '}(US: {fieldCoverage.missing_pb?.us || 0}, EU: {fieldCoverage.missing_pb?.eu || 0}, Other: {fieldCoverage.missing_pb?.other || 0})
                                        </Text>
                                      </li>
                                    </ul>
                                  </div>
                                )}
                                
                                {/* Dates checked log */}
                                {datesChecked.length > 0 && (
                                  <div>
                                    <Text strong style={{ display: 'block', marginBottom: 4 }}>📅 Dates Checked ({datesChecked.length}):</Text>
                                    <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 4, padding: 8 }}>
                                      {datesChecked.map((dateEntry, idx) => (
                                        <div key={idx} style={{ 
                                          marginBottom: 6, 
                                          padding: '4px 8px', 
                                          background: dateEntry.status === 'selected' ? '#f6ffed' : dateEntry.status === 'no_data' ? '#fff2e8' : '#fff7e6',
                                          borderRadius: 4,
                                          borderLeft: `3px solid ${dateEntry.status === 'selected' ? '#52c41a' : dateEntry.status === 'no_data' ? '#fa8c16' : '#faad14'}`,
                                        }}>
                                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Text strong>{dateEntry.date}</Text>
                                            <Tag 
                                              size="small" 
                                              color={dateEntry.status === 'selected' ? 'success' : dateEntry.status === 'no_data' ? 'warning' : 'orange'}
                                            >
                                              {dateEntry.status === 'selected' ? '✓ Selected' : dateEntry.status === 'no_data' ? 'No Data' : 'Insufficient'}
                                            </Tag>
                                          </div>
                                          <Text type="secondary" style={{ fontSize: 11 }}>{dateEntry.reason}</Text>
                                          {dateEntry.coverage && (
                                            <div style={{ fontSize: 10, marginTop: 2 }}>
                                              <Text type="secondary">
                                                Coverage: Overall {dateEntry.coverage.overall?.pct}%, 
                                                US {dateEntry.coverage.us?.pct}%, 
                                                EU {dateEntry.coverage.eu?.pct}%, 
                                                Other {dateEntry.coverage.other?.pct}%
                                              </Text>
                                            </div>
                                          )}
                                          {dateEntry.failed_regions && (
                                            <div style={{ marginTop: 2 }}>
                                              {dateEntry.failed_regions.map((region, rIdx) => (
                                                <Tag key={rIdx} color="red" style={{ fontSize: 10, margin: '2px 2px 0 0' }}>❌ {region}</Tag>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ),
                          }]}
                        />
                      )}
                    </div>
                  }
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              );
            })()}

            {/* Universe Breakdown (matching original SelectNowDrawer) */}
            {(selectedResult.universe_size?.universe_breakdown || selectedResult.universe_breakdown) && (() => {
              const ub = selectedResult.universe_size?.universe_breakdown || selectedResult.universe_breakdown;
              return (
                <Alert
                  message={
                    <Space wrap>
                      <Text strong>Universe Size:</Text>
                      {ub.initial && (
                        <Tooltip title="Initial universe from Ticker_Metadata based on region filter">
                          <Tag>Initial: {ub.initial.total} 
                            ({ub.initial.us} US, {ub.initial.eu} EU, {ub.initial.other} Other)</Tag>
                        </Tooltip>
                      )}
                      {ub.after_market_cap_filter && (
                        <Tooltip title="After applying minimum market cap filter">
                          <Tag color="blue">After MktCap: {ub.after_market_cap_filter.total}</Tag>
                        </Tooltip>
                      )}
                      {ub.after_fundamental_filter && (
                        <Tooltip title="Tickers with valid ROA and P/B data in Fundamentals_Historical">
                          <Tag color="purple">With Fundamentals: {ub.after_fundamental_filter.total}
                            ({ub.after_fundamental_filter.us} US, {ub.after_fundamental_filter.eu} EU, {ub.after_fundamental_filter.other} Other)</Tag>
                        </Tooltip>
                      )}
                      {ub.selected && (
                        <Tooltip title="Top stocks by composite score">
                          <Tag color="green">Selected: {ub.selected.total}
                            ({ub.selected.us} US, {ub.selected.eu} EU, {ub.selected.other} Other)</Tag>
                        </Tooltip>
                      )}
                    </Space>
                  }
                  type="info"
                  style={{ marginBottom: 16 }}
                  showIcon
                />
              );
            })()}

            {/* Data Quality Flags (matching original) */}
            {(() => {
              const flags = selectedResult.data_quality_flags?.flags || selectedResult.data_quality_flags;
              const hasFlags = selectedResult.data_quality_flags?.has_flags || (flags && Object.keys(flags).length > 0);
              const flaggedCount = selectedResult.data_quality_flags?.flagged_count || (flags ? Object.keys(flags).length : 0);
              const flaggedTickers = flags ? Object.keys(flags) : [];
              
              if (!hasFlags || flaggedCount === 0) return null;
              
              return (
                <Alert
                  message={
                    <Space>
                      <FlagFilled style={{ color: '#cf1322' }} />
                      <strong>Data Quality Flags</strong>
                      <Text>
                        {flaggedCount} ticker{flaggedCount > 1 ? 's' : ''} in results have data quality warnings
                      </Text>
                    </Space>
                  }
                  description={
                    <Text type="secondary">
                      Look for 🚩 flags in the ROA % and P/B columns. Hover over flagged values for details. 
                      Flagged tickers: {flaggedTickers.join(', ')}
                    </Text>
                  }
                  type="error"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              );
            })()}

            {/* Summary Cards (matching original SelectNowDrawer) */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title="Buy Trades"
                    value={selectedResult.summary?.buy_count || 0}
                    prefix="↑"
                    valueStyle={{ color: '#cf1322', fontSize: 24 }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title="Sell Trades"
                    value={selectedResult.summary?.sell_count || 0}
                    prefix="↓"
                    valueStyle={{ color: '#52c41a', fontSize: 24 }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title="Holds"
                    value={(selectedResult.recommendations || []).filter(r => r.trade === 'HOLD').length}
                    prefix="∷"
                    valueStyle={{ color: '#1890ff', fontSize: 24 }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small">
                  <Statistic
                    title="Net Cash Flow"
                    value={selectedResult.summary?.net_cash_flow || 0}
                    prefix="€"
                    valueStyle={{ 
                      color: (selectedResult.summary?.net_cash_flow || 0) < 0 ? '#cf1322' : '#52c41a', 
                      fontSize: 24 
                    }}
                    formatter={(val) => val.toLocaleString()}
                  />
                </Card>
              </Col>
            </Row>

            {/* Saved Result Metadata */}
            <Alert
              message={
                <Space>
                  <Text type="secondary">Saved:</Text>
                  <Text strong>{selectedResult.saved_at ? dayjs(selectedResult.saved_at).format('YYYY-MM-DD HH:mm:ss') : '-'}</Text>
                  <Text type="secondary">|</Text>
                  <Text type="secondary">As of Date:</Text>
                  <Text strong>{selectedResult.as_of_date || '-'}</Text>
                  <Text type="secondary">|</Text>
                  <Text type="secondary">Config ID:</Text>
                  <Text strong>#{selectedResult.configuration_id || '-'}</Text>
                  <Text type="secondary">|</Text>
                  <Text type="secondary">Run ID:</Text>
                  <Text strong>#{selectedResult.run_id || '-'}</Text>
                </Space>
              }
              type="info"
              style={{ marginBottom: 16, background: '#f0f5ff', border: '1px solid #d6e4ff' }}
            />

            {/* Full Configuration (4-column layout like original) */}
            {selectedResult.full_config && (
              <Collapse defaultActiveKey={[]} style={{ marginBottom: 16 }}>
                <Collapse.Panel 
                  header={
                    <Space>
                      <SettingOutlined />
                      <strong>Full Configuration</strong>
                      <Text type="secondary">(click to expand)</Text>
                    </Space>
                  } 
                  key="config"
                >
                  <Row gutter={16}>
                    <Col span={6}>
                      <Card size="small" title="Rebalancing">
                        <Descriptions size="small" column={1}>
                          <Descriptions.Item label="Frequency">{selectedResult.full_config?.rebalancing?.frequency_days} days</Descriptions.Item>
                          <Descriptions.Item label="ROA Weight (base)">{selectedResult.full_config?.rebalancing?.roa_weight_base}%</Descriptions.Item>
                          <Descriptions.Item label="P/B Weight (base)">{selectedResult.full_config?.rebalancing?.pb_weight_base}%</Descriptions.Item>
                          <Descriptions.Item label="ROA Weight (adj)">{selectedResult.full_config?.rebalancing?.roa_weight_adjusted}%</Descriptions.Item>
                          <Descriptions.Item label="P/B Weight (adj)">{selectedResult.full_config?.rebalancing?.pb_weight_adjusted}%</Descriptions.Item>
                          <Descriptions.Item label="Selection">Top {100 - (selectedResult.full_config?.rebalancing?.selection_percentile || 90)}%</Descriptions.Item>
                          <Descriptions.Item label="Sector Neutral">{selectedResult.full_config?.rebalancing?.sector_neutral ? 'Yes' : 'No'}</Descriptions.Item>
                          <Descriptions.Item label="Max Sector">{selectedResult.full_config?.rebalancing?.max_sector_exposure_pct}%</Descriptions.Item>
                          <Descriptions.Item label="Z-Score Base">{selectedResult.full_config?.rebalancing?.z_score_baseline}</Descriptions.Item>
                          <Descriptions.Item label="Excluded Sectors">
                            {(selectedResult.full_config?.rebalancing?.excluded_sectors || []).join(', ') || 'None'}
                          </Descriptions.Item>
                        </Descriptions>
                      </Card>
                    </Col>
                    <Col span={6}>
                      <Card size="small" title="Regime Adjustment">
                        <Descriptions size="small" column={1}>
                          <Descriptions.Item label="Enabled">
                            <Tag color={selectedResult.full_config?.regime_adjustment?.enabled ? 'green' : 'default'}>
                              {selectedResult.full_config?.regime_adjustment?.enabled ? 'Yes' : 'No'}
                            </Tag>
                          </Descriptions.Item>
                          <Descriptions.Item label="Current Regime">
                            <Tag color={selectedResult.full_config?.regime_adjustment?.current_regime === 'BULL' ? 'green' : selectedResult.full_config?.regime_adjustment?.current_regime === 'BEAR' ? 'red' : 'orange'}>
                              {selectedResult.full_config?.regime_adjustment?.current_regime || '-'}
                            </Tag>
                          </Descriptions.Item>
                          <Descriptions.Item label="Current VIX">{selectedResult.full_config?.regime_adjustment?.current_vix || '-'}</Descriptions.Item>
                          <Descriptions.Item label="Bull Threshold">&lt;{selectedResult.full_config?.regime_adjustment?.vix_bull_threshold || '-'}</Descriptions.Item>
                          <Descriptions.Item label="Bear Threshold">&gt;{selectedResult.full_config?.regime_adjustment?.vix_bear_threshold || '-'}</Descriptions.Item>
                          <Descriptions.Item label="Bull ROA/PB mult">{selectedResult.full_config?.regime_adjustment?.bull_roa_mult} / {selectedResult.full_config?.regime_adjustment?.bull_pb_mult}</Descriptions.Item>
                          <Descriptions.Item label="Bear ROA/PB mult">{selectedResult.full_config?.regime_adjustment?.bear_roa_mult} / {selectedResult.full_config?.regime_adjustment?.bear_pb_mult}</Descriptions.Item>
                          <Descriptions.Item label="Neutral ROA/PB mult">{selectedResult.full_config?.regime_adjustment?.neutral_roa_mult} / {selectedResult.full_config?.regime_adjustment?.neutral_pb_mult}</Descriptions.Item>
                          {/* DAS-2344 (B1): Surface DAS-2341 fields in this card */}
                          <Descriptions.Item label="Position mult (Bull/Neu/Bear)">
                            {selectedResult.full_config?.regime_adjustment?.bull_position_mult ?? '-'}
                            {' / '}
                            {selectedResult.full_config?.regime_adjustment?.neutral_position_mult ?? '-'}
                            {' / '}
                            {selectedResult.full_config?.regime_adjustment?.bear_position_mult ?? '-'}
                          </Descriptions.Item>
                          <Descriptions.Item label="Cash target % (Bull/Neu/Bear)">
                            {selectedResult.full_config?.regime_adjustment?.bull_cash_pct ?? '-'}
                            {' / '}
                            {selectedResult.full_config?.regime_adjustment?.neutral_cash_pct ?? '-'}
                            {' / '}
                            {selectedResult.full_config?.regime_adjustment?.bear_cash_pct ?? '-'}
                          </Descriptions.Item>
                          <Descriptions.Item label="Emergency de-risk (DAS-2341)">
                            <Tag color={selectedResult.full_config?.regime_adjustment?.emergency_derisk_on_bear_transition ? 'red' : 'default'}>
                              {selectedResult.full_config?.regime_adjustment?.emergency_derisk_on_bear_transition ? 'Enabled' : 'Disabled'}
                            </Tag>
                          </Descriptions.Item>
                          <Descriptions.Item label="Emergency lockout (days)">
                            {selectedResult.full_config?.regime_adjustment?.emergency_lockout_days
                              ?? selectedResult.full_config?.regime_adjustment?.hysteresis_days
                              ?? '-'}
                          </Descriptions.Item>
                        </Descriptions>
                      </Card>
                    </Col>
                    <Col span={6}>
                      <Card size="small" title="Transaction Costs">
                        <Descriptions size="small" column={1}>
                          <Descriptions.Item label="Buy Fixed">€{selectedResult.full_config?.transaction_costs?.buy_fixed_eur}</Descriptions.Item>
                          <Descriptions.Item label="Sell Fixed">€{selectedResult.full_config?.transaction_costs?.sell_fixed_eur}</Descriptions.Item>
                          <Descriptions.Item label="Buy %">{selectedResult.full_config?.transaction_costs?.buy_pct}%</Descriptions.Item>
                          <Descriptions.Item label="Sell %">{selectedResult.full_config?.transaction_costs?.sell_pct}%</Descriptions.Item>
                          <Descriptions.Item label="Buy bps">{selectedResult.full_config?.transaction_costs?.buy_bps}</Descriptions.Item>
                          <Descriptions.Item label="Sell bps">{selectedResult.full_config?.transaction_costs?.sell_bps}</Descriptions.Item>
                        </Descriptions>
                      </Card>
                      <Card size="small" title="Momentum Filter" style={{ marginTop: 8 }}>
                        <Descriptions size="small" column={1}>
                          <Descriptions.Item label="Enabled">
                            <Tag color={selectedResult.full_config?.momentum_filter?.enabled ? 'green' : 'default'}>
                              {selectedResult.full_config?.momentum_filter?.enabled ? 'Yes' : 'No'}
                            </Tag>
                          </Descriptions.Item>
                          <Descriptions.Item label="Min Return">{selectedResult.full_config?.momentum_filter?.min_return_pct}%</Descriptions.Item>
                          <Descriptions.Item label="Lookback">{selectedResult.full_config?.momentum_filter?.lookback_months} months</Descriptions.Item>
                          <Descriptions.Item label="Skip Recent">{selectedResult.full_config?.momentum_filter?.skip_recent_month ? 'Yes' : 'No'}</Descriptions.Item>
                        </Descriptions>
                      </Card>
                    </Col>
                    <Col span={6}>
                      <Card size="small" title="Position Sizing">
                        <Descriptions size="small" column={1}>
                          <Descriptions.Item label="Mode">{selectedResult.full_config?.position_sizing?.new_position_mode}</Descriptions.Item>
                          <Descriptions.Item label="Target Value">€{selectedResult.full_config?.position_sizing?.new_position_amount_eur?.toLocaleString() || '-'}</Descriptions.Item>
                        </Descriptions>
                      </Card>
                      <Card size="small" title="Filters" style={{ marginTop: 8 }}>
                        <Descriptions size="small" column={1}>
                          <Descriptions.Item label="Stock Region">{selectedResult.full_config?.currency_filters?.stock_region || '-'}</Descriptions.Item>
                          <Descriptions.Item label="Liquidity Filter">
                            <Tag color={selectedResult.full_config?.liquidity_filter?.enabled ? 'green' : 'default'}>
                              {selectedResult.full_config?.liquidity_filter?.enabled ? 'Yes' : 'No'}
                            </Tag>
                          </Descriptions.Item>
                          {selectedResult.full_config?.liquidity_filter?.enabled && (
                            <Descriptions.Item label="Min Market Cap">€{(selectedResult.full_config?.liquidity_filter?.min_market_cap_eur / 1e9)?.toFixed(1)}B</Descriptions.Item>
                          )}
                          <Descriptions.Item label="Allow Negative Cash">{selectedResult.full_config?.constraints?.allow_negative_cash ? 'Yes' : 'No'}</Descriptions.Item>
                        </Descriptions>
                      </Card>
                    </Col>
                  </Row>
                  
                  {/* Scoring Formula */}
                  <div style={{ 
                    marginTop: 12, 
                    padding: '8px 12px', 
                    background: '#e6f7ff', 
                    borderRadius: 4,
                    fontSize: 13,
                    fontFamily: 'monospace'
                  }}>
                    <Text strong>Scoring Formula: </Text>
                    <Text code style={{ fontSize: 12 }}>
                      Score = ({selectedResult.full_config?.rebalancing?.roa_weight_adjusted/100} × ROA_Z) − ({selectedResult.full_config?.rebalancing?.pb_weight_adjusted/100} × P/B_Z)
                    </Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Weights adjusted from base ({selectedResult.full_config?.rebalancing?.roa_weight_base}%/{selectedResult.full_config?.rebalancing?.pb_weight_base}%) 
                      based on {selectedResult.full_config?.regime_adjustment?.current_regime || 'NEUTRAL'} regime 
                      (VIX={selectedResult.full_config?.regime_adjustment?.current_vix || '-'}).
                    </Text>
                  </div>
                </Collapse.Panel>
              </Collapse>
            )}

            {/* Notes */}
            {selectedResult.notes && (
              <Alert
                message={<Text strong>Notes</Text>}
                description={selectedResult.notes}
                type="info"
                style={{ marginBottom: 16 }}
              />
            )}

            {/* DAS-2344 (L2): Suggested Emergency Trim — shown when the live regime
                has just transitioned defensively AND the config has emergency-derisk
                enabled. Mirrors the backtest engine's mid-cycle trim recommendation. */}
            {selectedResult.emergency_derisk_recommendation && (
              <Card
                size="small"
                style={{ marginBottom: 16, borderColor: '#cf1322' }}
                title={
                  <Space>
                    <span style={{ color: '#cf1322', fontWeight: 'bold' }}>
                      ⚠️ Suggested Emergency Trim (DAS-2341/2344)
                    </span>
                    <Tag color="red">
                      {selectedResult.emergency_derisk_recommendation.transition_from || '?'}
                      {' → '}
                      {selectedResult.emergency_derisk_recommendation.transition_to_live || selectedResult.emergency_derisk_recommendation.regime_to}
                    </Tag>
                  </Space>
                }
              >
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="The live market regime has shifted to a more defensive state since the last stored regime."
                  description={
                    <span>
                      The backtest engine would trim positions to reach the regime cash target
                      (<strong>{selectedResult.emergency_derisk_recommendation.target_cash_pct}%</strong>)
                      between scheduled rebalances. Suggested action: place the sells listed below at Saxo,
                      OR wait for the next scheduled rebalance if you prefer a full rotation.
                    </span>
                  }
                />
                <Descriptions size="small" column={3} style={{ marginBottom: 12 }}>
                  <Descriptions.Item label="Equity (pre-trim)">
                    €{Number(selectedResult.emergency_derisk_recommendation.equity_eur_pre).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </Descriptions.Item>
                  <Descriptions.Item label="Cash % (pre-trim)">
                    {selectedResult.emergency_derisk_recommendation.cash_pct_pre}%
                  </Descriptions.Item>
                  <Descriptions.Item label="Cash to raise (est.)">
                    €{Number(selectedResult.emergency_derisk_recommendation.cash_raised_eur).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </Descriptions.Item>
                  <Descriptions.Item label="Sells">
                    {selectedResult.emergency_derisk_recommendation.sells_count}
                  </Descriptions.Item>
                  <Descriptions.Item label="Est. fees">
                    €{Number(selectedResult.emergency_derisk_recommendation.fees_eur).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </Descriptions.Item>
                  <Descriptions.Item label="Target regime">
                    {selectedResult.emergency_derisk_recommendation.regime_to}
                  </Descriptions.Item>
                </Descriptions>
                <Table
                  size="small"
                  pagination={false}
                  dataSource={(selectedResult.emergency_derisk_recommendation.sells || []).map((s, idx) => ({ key: idx, ...s }))}
                  columns={[
                    { title: 'Ticker', dataIndex: 'ticker', key: 'ticker' },
                    { title: 'Sell Qty', dataIndex: 'qty', key: 'qty', align: 'right' },
                    { title: 'Price (EUR)', dataIndex: 'price', key: 'price', align: 'right',
                      render: v => `€${Number(v).toFixed(2)}` },
                    { title: 'Trade value', dataIndex: 'trade_value_eur', key: 'trade_value_eur', align: 'right',
                      render: v => `€${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}` },
                    { title: 'Fee', dataIndex: 'fee', key: 'fee', align: 'right',
                      render: v => `€${Number(v).toFixed(2)}` },
                    { title: 'Net proceeds', dataIndex: 'proceeds', key: 'proceeds', align: 'right',
                      render: v => `€${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}` },
                  ]}
                />
              </Card>
            )}

            {/* Recommendations Table */}
            <Card
              title={
                <Space>
                  <UnorderedListOutlined />
                  <span>Recommendations ({(selectedResult.recommendations || []).length})</span>
                </Space>
              }
              size="small"
            >
              {/* DAS-1869: Ticker search input - placed above the table */}
              <Box sx={{ width: 300, marginBottom: 16 }}>
                <Autocomplete
                  freeSolo
                  disableClearable
                  options={tickerSearchOptions}
                  inputValue={tickerSearchValue}
                  onInputChange={async (event, newInputValue) => {
                    setTickerSearchValue(newInputValue);
                    if (!newInputValue) {
                      setTickerSearchOptions([]);
                      setFilteredTicker(null);
                      return;
                    }
                    try {
                      const response = await api.get(`/search_suggestions?query=${newInputValue}`);
                      const autoCompleteOptions = response.data.map((suggestion) => ({
                        label: suggestion,
                      }));
                      setTickerSearchOptions(autoCompleteOptions);
                    } catch (error) {
                      console.error('Error fetching search suggestions:', error);
                      setTickerSearchOptions([]);
                    }
                  }}
                  onChange={(event, value) => {
                    if (value) {
                      const ticker = value.label.split(' - ')[0].toUpperCase();
                      setFilteredTicker(ticker);
                      setTickerSearchValue('');
                      setTickerSearchOptions([]);
                      // Scroll to the row in the table
                      setTimeout(() => {
                        const rowElement = document.querySelector(`[data-row-key*="${ticker}"]`);
                        if (rowElement) {
                          rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          // Highlight the row
                          rowElement.style.backgroundColor = '#fffbe6';
                          setTimeout(() => {
                            rowElement.style.backgroundColor = '';
                          }, 2000);
                        }
                      }, 100);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search ticker..."
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: <SearchOutlined style={{ marginRight: 8, color: '#8c8c8c' }} />,
                      }}
                    />
                  )}
                />
              </Box>
              <Table
                rowKey={(record) => `${record.ticker}-${record.trade}`}
                columns={recommendationColumns}
                dataSource={
                  // DAS-1870: Use modified recommendations if swaps were made
                  (() => {
                    const recs = modifiedRecommendations || selectedResult.recommendations || [];
                    return filteredTicker
                      ? recs.filter(rec => rec.ticker && rec.ticker.toUpperCase() === filteredTicker)
                      : recs;
                  })()
                }
                pagination={{ pageSize: 15, showSizeChanger: true }}
                size="small"
                scroll={{ x: 1600 }}
                onRow={(record) => ({
                  'data-row-key': `${record.ticker}-${record.trade}`,
                })}
              />
            </Card>
          </div>
        ) : (
          <Empty description="No result selected" />
        )}
      </Drawer>

      {/* Edit Modal */}
      <Modal
        title="Edit Result"
        open={editModalOpen}
        onCancel={() => setEditModalOpen(false)}
        onOk={handleEditSave}
        okText="Save Changes"
        confirmLoading={editLoading}
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8 }}>
            <Text strong>Name:</Text>
          </div>
          <Input
            placeholder="Enter a name for this result"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            maxLength={255}
          />
        </div>
        <div>
          <div style={{ marginBottom: 8 }}>
            <Text strong>Notes:</Text>
          </div>
          <Input.TextArea
            placeholder="Add any notes..."
            value={editNotes}
            onChange={(e) => setEditNotes(e.target.value)}
            rows={4}
            maxLength={500}
            showCount
          />
        </div>
      </Modal>

      {/* SearchResultModal for ticker details */}
      {searchResultModal.open && (
        <ThreeTablesModal
          tickerValue={searchResultModal.ticker}
          open={searchResultModal.open}
          onClose={() => setSearchResultModal({ open: false, ticker: null })}
          userId={getUserId()}
        />
      )}
      
      {/* DAS-2157: Alert detail dialog */}
      <Dialog
        open={alertDialogOpen}
        onClose={() => { setAlertDialogOpen(false); setSelectedAlertForDialog(null); }}
        maxWidth={false}
        fullWidth={false}
        PaperProps={{
          style: { maxHeight: '90vh', overflow: 'auto', width: 'auto', maxWidth: '90vw' }
        }}
      >
        <DialogTitle>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Alert Details — {selectedAlertForDialog?.ticker}</span>
            <Button type="text" onClick={() => { setAlertDialogOpen(false); setSelectedAlertForDialog(null); }} style={{ minWidth: 'auto' }}>✕</Button>
          </div>
        </DialogTitle>
        <DialogContent>
          {selectedAlertForDialog && (
            <AlertCard
              alert={selectedAlertForDialog}
              onClose={() => { setAlertDialogOpen(false); setSelectedAlertForDialog(null); }}
              onCheckboxChange={() => {}}
              onFollowedUpChange={() => {}}
              onReinvestmentCheck={() => {}}
              onTickerClick={(t) => { setAlertDialogOpen(false); setSearchResultModal({ open: true, ticker: t }); }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SelectNowResultsTab;

